/**
 * Structured data, built in one place so nothing is ever written inline.
 *
 * The rule every builder here follows: a node may only state what the rendered
 * page shows. A reading time in `timeRequired` is the reading time printed on
 * the page. A question in `hasPart` is a question a reader can see and link to.
 * Nothing here invents a rating, a review, an author, or a price, because the
 * fastest way to lose a rich result is to be caught claiming one.
 *
 * Two site-wide nodes carry stable ids that page nodes reference rather than
 * repeat, so a crawler assembling the site sees one organization rather than
 * one per page.
 */

import type { Article } from "../shared";
import {
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_TAGLINE,
  SITE_URL,
} from "../shared";
import { LOCALES, LOCALE_TAGS, type Locale } from "../i18n/locales";
import { headingId } from "../domain/richText";
import type { ArticleFaq } from "../shared/types";

export type JsonLd = Record<string, unknown>;

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** The publisher every page-level node points at. */
export function buildOrganizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    description: SITE_TAGLINE,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/favicon.svg`,
      caption: SITE_NAME,
    },
  };
}

export function buildWebSiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    description: SITE_DESCRIPTION,
    // The site as a whole is in three languages; a page node below says which
    // one that page is.
    inLanguage: LOCALES.map((locale) => LOCALE_TAGS[locale]),
    publisher: { "@id": ORGANIZATION_ID },
  };
}

export interface Crumb {
  name: string;
  url: string;
}

/**
 * The same array feeds this node and the trail rendered above the article, so a
 * breadcrumb a crawler is told about is a breadcrumb a reader can click.
 */
export function buildBreadcrumbJsonLd(crumbs: readonly Crumb[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

export interface CollectionEntry {
  name: string;
  url: string;
  description: string;
}

export function buildCollectionJsonLd(input: {
  url: string;
  name: string;
  description: string;
  locale: Locale;
  entries: readonly CollectionEntry[];
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${input.url}#collection`,
    url: input.url,
    name: input.name,
    description: input.description,
    inLanguage: LOCALE_TAGS[input.locale],
    isPartOf: { "@id": WEBSITE_ID },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: input.entries.length,
      itemListElement: input.entries.map((entry, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: entry.url,
        name: entry.name,
        description: entry.description,
      })),
    },
  };
}

/**
 * The one place an FAQPage node is emitted. Google stopped showing FAQ rich
 * results in May 2026, so this is a classification of a page that genuinely is
 * a list of questions, not a bid for a result decoration. Article questions
 * stay attached to their article instead, as real parts of that work.
 */
export function buildFaqPageJsonLd(input: {
  url: string;
  name: string;
  description: string;
  locale: Locale;
  faqs: readonly ArticleFaq[];
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${input.url}#faq`,
    url: input.url,
    name: input.name,
    description: input.description,
    inLanguage: LOCALE_TAGS[input.locale],
    isPartOf: { "@id": WEBSITE_ID },
    mainEntity: input.faqs.map((faq) => ({
      "@type": "Question",
      "@id": `${input.url}#${faqId(faq.question)}`,
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

/** The anchor a visible question and its structured twin both use. */
export function faqId(question: string): string {
  return `faq-${headingId(question)}`;
}

export function buildArticleJsonLd(input: {
  article: Article;
  url: string;
  readingTimeMinutes: number;
}): JsonLd {
  const { article, url } = input;
  const articleId = `${url}#article`;
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": articleId,
    url,
    headline: article.title,
    description: article.description,
    inLanguage: LOCALE_TAGS.en,
    datePublished: article.date,
    dateModified: article.updated,
    timeRequired: `PT${input.readingTimeMinutes}M`,
    articleSection: article.category,
    keywords: [article.primaryKeyword, ...article.secondaryKeywords].join(", "),
    // A byline nobody can see on the page would be a claim about a person who
    // is not there, so an unsigned article is published by the site itself.
    author:
      article.author === null
        ? { "@id": ORGANIZATION_ID }
        : { "@type": "Person", name: article.author },
    publisher: { "@id": ORGANIZATION_ID },
    isPartOf: { "@id": WEBSITE_ID },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  if (article.sources.length > 0) {
    node.citation = article.sources.map((source) => ({
      "@type": "CreativeWork",
      name: source.title,
      url: source.url,
      publisher: { "@type": "Organization", name: source.authority },
    }));
  }

  if (article.faqs.length > 0) {
    node.hasPart = article.faqs.map((faq) => {
      const questionUrl = `${url}#${faqId(faq.question)}`;

      return {
        "@type": "Question",
        "@id": questionUrl,
        url: questionUrl,
        name: faq.question,
        inLanguage: LOCALE_TAGS.en,
        isPartOf: { "@id": articleId },
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
          inLanguage: LOCALE_TAGS.en,
        },
      };
    });
  }

  return node;
}

/**
 * The app itself, on the page that presents it. The zero-price offer is only
 * honest while the course genuinely costs nothing, which is exactly what the
 * homepage says today. Delete the offer the day a card is asked for.
 */
export function buildWebApplicationJsonLd(
  url: string,
  locale: Locale,
  description: string,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": `${url}#webapplication`,
    name: SITE_NAME,
    url,
    description,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Any modern web browser",
    inLanguage: LOCALE_TAGS[locale],
    isPartOf: { "@id": WEBSITE_ID },
    provider: { "@id": ORGANIZATION_ID },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
    },
  };
}
